/* eslint-disable import/first */
require('libs/client/init-env').init() // eslint-disable-line

import './fonts.css'
import './base.scss'
import './help-center/gladly.scss'
// eslint-disable-next-line no-restricted-imports
import '@mc/design-system/src/styles/theme.css'

import React from 'react'
import App, { AppContext, AppInitialProps } from 'next/app'
import Error from 'next/error'
import { NextRouter } from 'next/router'
import { SWRConfig } from 'swr'
import { ParsedUrlQuery } from 'querystring'
import { QueryClientProvider } from '@tanstack/react-query'

import { I18nextProvider } from '@mc/i18n'
import { setGeo, setRegion } from '@mc/geo'
import { setFeatureFlips } from '@mc/feature-flip'
import { FeatureFlipsProvider } from '@mc/feature-flip-react'
import { setExperiments, setExperimentsVariables } from '@mc/experiments'
import { ExperimentsProvider } from '@mc/experiments-react'
import { PromoBanner } from '@mc/promos'
import { StaticDataProvider } from '@mc/static-data-react'
import { GeoProvider } from '@mc/geo-react'
import { ErrorPage } from '@mc/error-page'
import { setServer, trackPage } from '@mc/track-event'
import ErrorBoundary from '@mc/error-boundary'
import { reportError as reportBrowserError, setUser } from '@mc/monitoring'
import { Head, NewRelic, Segment, Consent, AnonymousId } from '@mc/next-common'
import { getUserAttributes } from '@mc/next-user-attributes'
import { reportError } from '@mc/next-newrelic'
import { getGrowthBookFlags } from '@mc/next-growthbook'

import i18nInstance from 'i18n/i18nInstance'
import { requestSideEffects } from 'libs/request-side-effects'
import { fetcher } from 'libs/client/fetchers'
import { queryClient } from 'libs/client/query-client'
import { getUrlWithoutQueryParams } from 'libs/utils'
import type { NavigationProps } from 'types/components/pages'
import { NotFoundPage } from 'components/NotFoundPage/NotFoundPage'
import { SegmentTrackingProvider } from 'libs/events/SegmentTrackingProvider'
import { LoginSuccessDetection } from 'components/LoginSuccessDetection'
import { RouteAnnouncerLandmark } from 'components/RouteAnnouncerLandmark'
import { getSSRQuizOnlyOnceCookie } from 'libs/utils/quizOnlyOnce'
import { trackCoreWebVitals } from 'libs/utils/coreWebVitals'
import HotJar from 'components/Head/HotJar'
import { Turnstile } from 'components/Head/Turnstile'
import { Maze } from 'components/Head/Maze'
import { Cheq } from 'components/Head/Cheq'
import { ContentSquare } from 'components/Head/ContentSquare'
import { FontPreload } from 'components/Head/FontPreload'

const THEME_PAGE_CLASSES: Record<string, string> = {
  'mc-theme-sessions': 'mc-theme-sessions mc-theme-light',
}

setServer('nextjs')

type PageProps = {
  userAttributes: ReturnType<typeof getUserAttributes>
  experiments: Awaited<
    ReturnType<typeof getGrowthBookFlags>
  >['growthBookExperiments']
  experimentsVariables: Awaited<
    ReturnType<typeof getGrowthBookFlags>
  >['growthBookExperimentsVariables']
  featureFlips: Awaited<
    ReturnType<typeof getGrowthBookFlags>
  >['growthBookFeatureFlips']
  error?: globalThis.Error & { statusCode: number }
  annualPass?: unknown
  categories?: unknown
  trial?: unknown
  ctaNextPage?: unknown
  primaryCtaOverride?: unknown
}

type Props =
  | (AppInitialProps<PageProps> & {
      query?: ParsedUrlQuery
      pathname?: string
    })
  | {
      serverSideInitialPropsError: true
    }

type ComponentType = React.ComponentType<PageProps> & {
  pageSettings?: {
    title?: string
    pageName?: string
    analyticsLabel?: string
    pageLoadIntegrations?: Record<string, boolean>
    theme?: string
    footer?: React.ComponentType | null
    navigation?: React.ComponentType<NavigationProps> | null
    showPromo?: boolean
    noindex?: boolean
  }
  getLayout?: (
    page: React.ReactElement,
    config: { pageProps?: Partial<PageProps> },
  ) => React.ReactNode
}

declare global {
  interface Window {
    _mc?: {
      cwvV2?: boolean
      performanceTimings?: boolean
    }
  }
}

trackCoreWebVitals()

function renderPageContent(
  pageProps: Partial<PageProps>,
  content: React.ReactNode,
): React.ReactNode {
  if (!pageProps.error) return content
  if (pageProps.error.statusCode === 404) return <NotFoundPage />
  return (
    <Error
      statusCode={pageProps.error.statusCode}
      title={pageProps.error.message}
    />
  )
}

class Layout extends React.Component<
  Props & { Component: ComponentType; router: NextRouter; pathname: string }
> {
  constructor(
    props: Props & {
      Component: ComponentType
      router: NextRouter
      pathname: string
    },
  ) {
    super(props)
    try {
      if (!('serverSideInitialPropsError' in props)) {
        const {
          userAttributes,
          experiments,
          experimentsVariables,
          featureFlips,
        } = props.pageProps

        // set lib data for react
        setGeo(userAttributes.country)
        setRegion(userAttributes.region)
        setFeatureFlips(featureFlips)
        setExperiments(experiments)
        setExperimentsVariables(experimentsVariables)
      }
    } catch (e) {
      reportError(e, {
        rootLayout: 'true',
        location: 'Layout constructor',
        message: 'Error occurred in _app.page.js Layout constructor',
      })
    }
  }

  static async getInitialProps(appContext: AppContext): Promise<Props> {
    const appProps: AppInitialProps<PageProps> =
      await App.getInitialProps(appContext)
    const { query, req, res, pathname } = appContext.ctx

    // runs on server only
    if (req && res) {
      try {
        requestSideEffects(req, res, appContext.router)

        const userAttributes = getUserAttributes(
          appContext.ctx,
          appContext.router,
        )
        // userId is the anonymous ID (see getUserAttributes)
        if (userAttributes.userId) {
          setUser(userAttributes.userId)
        }
        const {
          growthBookExperiments,
          growthBookExperimentsVariables,
          growthBookFeatureFlips,
        } = await getGrowthBookFlags(userAttributes, appContext.ctx)
        // set lib data for getServerSideProps
        setGeo(userAttributes.country)
        setRegion(userAttributes.region)
        setFeatureFlips({ ...growthBookFeatureFlips })
        setExperiments({ ...growthBookExperiments })
        setExperimentsVariables({
          ...growthBookExperimentsVariables,
        })

        // TODO(PEN-6968): rename `ctaNextPage` to a quiz-specific name (e.g.
        // quizNextPageHref). It only ever holds the quiz-only-once cookie
        // value — do not repurpose it for other CTA redirects.
        const ctaNextPage = await getSSRQuizOnlyOnceCookie(appContext.ctx)
        return {
          ...appProps,
          pageProps: {
            ...appProps.pageProps,
            experiments: {
              ...growthBookExperiments,
            },
            experimentsVariables: {
              ...growthBookExperimentsVariables,
            },
            featureFlips: {
              ...growthBookFeatureFlips,
            },
            userAttributes,
            ctaNextPage,
          },
          query,
          pathname,
        }
      } catch (e) {
        reportError(e, {
          rootLayout: 'true',
          location: 'getInitialProps',
          message: 'Error occurred in _app.page.js getInitialProps',
        })
        return {
          serverSideInitialPropsError: true,
        }
      }
    }

    return { ...appProps }
  }

  render() {
    if ('serverSideInitialPropsError' in this.props) {
      return <ErrorPage />
    }

    const { Component, pageProps, query, router, pathname } = this.props

    const getLayout = Component.getLayout ?? ((page) => page)

    const pageTitle = Component.pageSettings?.title || 'MasterClass'
    const {
      annualPass,
      categories,
      trial,
      userAttributes,
      ctaNextPage,
      primaryCtaOverride,
    } = pageProps
    const promoCode = userAttributes?.promo_code
    const isLoggedIn = userAttributes?.is_logged_in
    const country = userAttributes?.country
    const region = userAttributes?.region
    const experimentsProviderValues = {
      experiments: pageProps?.experiments,
      experimentsVariables: pageProps?.experimentsVariables,
    }
    const featureFlips = pageProps?.featureFlips

    const cwvTracking =
      pageProps?.experiments?.cwv_reporting_onload === 'variant_1'

    const performanceTimings =
      pageProps?.experiments?.ev_perf_metrics === 'variant_1'

    const PageNavigation = Component.pageSettings?.navigation
    const PageFooter = Component.pageSettings?.footer
    const pageTheme = Component.pageSettings?.theme
    const mcPageClassName = `mc-page ${pageTheme ? THEME_PAGE_CLASSES[pageTheme] || '' : ''}`

    return (
      <ErrorBoundary
        fallback={() => <ErrorPage />}
        onError={(e) =>
          reportBrowserError(new globalThis.Error('Layout ErrorBoundary'), {
            error: String(e),
            stack: e?.stack as string,
          })
        }
      >
        <AnonymousId ajsAnonId={userAttributes?.userId} />
        <StaticDataProvider
          value={{
            annualPass,
            categories,
            trial,
            isLoggedIn,
            query,
            pathname,
            promoCode,
            ctaNextPage,
            primaryCtaOverride,
            userAttributes,
          }}
          providerKey={router.asPath}
        >
          <I18nextProvider i18n={i18nInstance} defaultNS='@mc/next'>
            <GeoProvider value={{ country, region }}>
              <FeatureFlipsProvider value={featureFlips}>
                <ExperimentsProvider value={experimentsProviderValues}>
                  <Head>
                    <title>{pageTitle}</title>
                    {Component.pageSettings?.noindex && (
                      <meta name='robots' content='noindex' />
                    )}
                    <link rel='icon' href='/_next-public/favicon.ico' />
                    <link
                      key='canonical'
                      rel='canonical'
                      href={getUrlWithoutQueryParams(router.asPath)}
                    />
                    <script
                      dangerouslySetInnerHTML={{
                        // pen-5271_v1: core web vital reporting, updated method
                        __html: `window._mc = { cwvV2: ${cwvTracking}, performanceTimings: ${performanceTimings} };`,
                      }}
                    />
                  </Head>
                  <FontPreload />
                  <HotJar />
                  <NewRelic />
                  <Segment
                    analyticsLabel={Component.pageSettings?.analyticsLabel}
                    analyticsIntegrations={
                      Component.pageSettings?.pageLoadIntegrations
                    }
                    trackPage={trackPage}
                  />
                  <Consent />
                  <Turnstile />
                  <Maze />
                  <ContentSquare />
                  <Cheq />
                  <SWRConfig value={{ fetcher }}>
                    <QueryClientProvider client={queryClient}>
                      <SegmentTrackingProvider>
                        <LoginSuccessDetection>
                          <div className={mcPageClassName}>
                            {PageNavigation && (
                              <PageNavigation
                                appUser={
                                  pageProps.userAttributes?.membership !==
                                  'FREE'
                                }
                              />
                            )}
                            {Component.pageSettings?.showPromo && (
                              <PromoBanner pathname={pathname} />
                            )}
                            <main className='mc-page__content' id='mc-main'>
                              {renderPageContent(
                                pageProps,
                                getLayout(<Component {...pageProps} />, {
                                  pageProps,
                                }),
                              )}
                              <RouteAnnouncerLandmark />
                            </main>
                            {PageFooter && <PageFooter />}
                          </div>
                        </LoginSuccessDetection>
                      </SegmentTrackingProvider>
                    </QueryClientProvider>
                  </SWRConfig>
                </ExperimentsProvider>
              </FeatureFlipsProvider>
            </GeoProvider>
          </I18nextProvider>
        </StaticDataProvider>
      </ErrorBoundary>
    )
  }
}

export default Layout
